import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { hasPendingAiQuestion } from "@/lib/pendingAi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Eye, EyeOff, ShieldCheck } from "lucide-react";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [quickOk, setQuickOk] = useState(false);
  const [adminCode, setAdminCode] = useState("");
  const [showAdminCode, setShowAdminCode] = useState(false);
  const { login, quickAdmin } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const rawRedirect = searchParams.get("redirect");
  const redirectTo = rawRedirect && rawRedirect.startsWith("/") && !rawRedirect.startsWith("//") ? rawRedirect : null;

  useEffect(() => {
    api.get("/auth/quick-admin/available")
      .then((r) => setQuickOk(!!r.data?.available))
      .catch(() => setQuickOk(false));
  }, []);

  const goByRole = (u) => {
    if (localStorage.getItem("macarte_draft")) { navigate("/ma-carte"); return; }
    if (redirectTo) { navigate(redirectTo); return; }
    if (u.role === "admin" || u.role === "super_admin") navigate("/admin");
    else if (u.role === "professional") navigate("/pro");
    else if (u.role === "organization") navigate("/organisme");
    else if (u.role === "association") navigate("/espace-association");
    else if (u.role === "club") navigate("/espace-club");
    else navigate(hasPendingAiQuestion() ? "/mon-assistant-ia" : "/compte");
  };

  const quick = async (e) => {
    if (e) e.preventDefault();
    if (!adminCode.trim()) { toast.error("Saisissez votre identifiant administrateur."); return; }
    setLoading(true);
    try { const u = await quickAdmin(adminCode.trim()); toast.success("Connexion admin ✅"); goByRole(u); }
    catch { toast.error("Identifiant administrateur invalide."); }
    finally { setLoading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success("Connecté !");
      goByRole(u);
    } catch (e) { toast.error("Email ou mot de passe incorrect."); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-16">
      <h1 className="font-heading text-3xl font-extrabold text-center">Connexion</h1>
      <form onSubmit={submit} className="mt-8 bg-white border border-slate-200 rounded-2xl p-6 space-y-4" data-testid="login-form">
        <div>
          <Label>Email</Label>
          <Input type="email" required value={email} onChange={(e)=>setEmail(e.target.value)} data-testid="login-email-input"/>
        </div>
        <div>
          <Label>Mot de passe</Label>
          <div className="mt-1 relative">
            <Input type={showPwd ? "text" : "password"} required value={password} onChange={(e)=>setPassword(e.target.value)} className="pr-10" data-testid="login-password-input"/>
            <button type="button" onClick={()=>setShowPwd(v=>!v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-900"
                    aria-label={showPwd ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                    data-testid="login-password-toggle">
              {showPwd ? <EyeOff size={16}/> : <Eye size={16}/>}
            </button>
          </div>
        </div>
        <Button type="submit" disabled={loading} className="w-full btn-shine text-white h-11" data-testid="login-submit-btn">
          {loading ? "Connexion…" : "Se connecter"}
        </Button>
        <div className="text-center">
          <Link to="/mot-de-passe-oublie" className="text-sm text-[#E05638] font-semibold hover:underline"
                data-testid="login-forgot-password-link">
            Mot de passe oublié ?
          </Link>
        </div>
        <div className="text-center text-sm text-slate-500">
          Pas encore de compte ? <Link to="/inscription" className="text-[#E05638] font-semibold">Créer un compte</Link>
        </div>
      </form>

      {quickOk && (
        <form onSubmit={quick} className="mt-6 pt-5 border-t border-slate-200" data-testid="login-admin-block">
          <div className="flex items-center gap-2 text-xs font-semibold text-slate-500 uppercase tracking-widest">
            <ShieldCheck size={14}/> Espace administrateur
          </div>
          <div className="mt-2 relative">
            <Input type={showAdminCode ? "text" : "password"} value={adminCode}
                   onChange={(e)=>setAdminCode(e.target.value)}
                   placeholder="Identifiant administrateur"
                   className="pr-10" data-testid="login-admin-code"/>
            <button type="button" onClick={()=>setShowAdminCode(v=>!v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-900"
                    aria-label={showAdminCode ? "Masquer" : "Afficher"} data-testid="login-admin-code-toggle">
              {showAdminCode ? <EyeOff size={16}/> : <Eye size={16}/>}
            </button>
          </div>
          <Button type="submit" disabled={loading} variant="outline" className="mt-3 w-full h-10" data-testid="login-admin-submit">
            <ShieldCheck size={15} className="mr-1"/> Connexion administrateur
          </Button>
        </form>
      )}
    </div>
  );
}
